import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { LocaleProvider } from "./i18n/LocaleProvider";
import "./index.css";

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LocaleProvider>
      <App />
    </LocaleProvider>
  </StrictMode>,
);

// Register the service worker so the app is installable and works offline, and
// keep it fresh. An installed app (PWA) has no address bar or pull-to-refresh
// and the OS usually resumes it from memory instead of relaunching, so without
// this it can run a stale build for days. We therefore (1) re-check for a new
// service worker whenever the app regains focus and hourly, and (2) reload once
// the moment a new worker takes control — the sw.js `skipWaiting` + `clients.
// claim` hand control straight over, so this reload swaps in the fresh bundle
// instead of the stale in-memory one. Uses the Vite base URL so the scope is
// correct on GitHub Pages.
if (!import.meta.env.PROD && "serviceWorker" in navigator) {
  // Dev servers must NEVER run under a service worker: a worker registered by
  // an earlier session on this localhost port serves a stale precache whose
  // asset hashes no longer exist, and the page loads blank. Unregister
  // anything already claimed on this origin so a poisoned port self-heals on
  // the next successful load.
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const reg of regs) reg.unregister();
  });
} else if ("serviceWorker" in navigator) {
  // Reload once when a *new* worker takes over. On the very first visit there's
  // no prior controller (the first worker claims the page) — that's not an
  // update, so we skip it. `refreshing` guards against a reload loop.
  let refreshing = false;
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing || !hadController) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`)
      .then((reg) => {
        const check = () => reg.update().catch(() => {});
        // Look for a new deploy when the app comes back to the foreground
        // (covers a PWA resumed after being backgrounded) and hourly while open.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        window.setInterval(check, 60 * 60 * 1000);
      })
      .catch(() => {
        /* offline support is best-effort; ignore registration failures */
      });
  });
}
